import { AIONZ_DIALOG_DATA, AionzDialogModule, AionzDialogRef, HeaderBottomSheetComponent, InputSearchComponent } from '@aionzdev/design-system';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { InfiniteScrollDirective } from 'ngx-infinite-scroll';
import { debounceTime, distinctUntilChanged, finalize, take } from 'rxjs/operators';
import { ISelectorListData } from './interfaces/selector-list-data.interface';
import { SelectorListItem } from './interfaces/selector-list-item.interface';
import { AbstractSelectorListService } from './services/abstract-selector-list.service';

@Component({
  selector: 'aionz-selector-list',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatIconModule,
    AionzDialogModule,
    MatProgressSpinnerModule,
    InfiniteScrollDirective,
    HeaderBottomSheetComponent,
    InputSearchComponent
  ],
  templateUrl: './selector-list.component.html',
  styleUrls: ['./selector-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SelectorListComponent {
  private dialogRef = inject(AionzDialogRef<SelectorListComponent>);
  private service = inject(AbstractSelectorListService);

  public data = signal(inject<ISelectorListData>(AIONZ_DIALOG_DATA));

  public items = signal<SelectorListItem[]>([]);
  public isLoading = signal(false);
  public isEnd = signal(false);

  private currentPage = 1;
  private readonly take = 10;

  public searchControl = new FormControl('', { nonNullable: true });

  constructor() {
    this.searchControl.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntilDestroyed())
      .subscribe(value => {
        this.resetAndFind(value);
      });

    this.find();
  }

  private resetAndFind(search: string) {
    this.currentPage = 1;
    this.items.set([]);
    this.isEnd.set(false);
    this.find(search);
  }

  private find(search: string = this.searchControl.value) {
    if (this.isEnd()) return;

    this.isLoading.set(true);

    this.service
      .getItems(
        {
          search,
          currentPage: this.currentPage,
          take: this.take
        },
        this.data().currentBuyerId
      )
      .pipe(
        take(1),
        finalize(() => {
          this.isLoading.set(false);
        })
      )
      .subscribe(res => {
        this.items.update(prev => [...prev, ...res.items]);

        if (this.currentPage >= res.pagesCount || res.items.length < this.take) {
          this.isEnd.set(true);
        }
      });
  }

  onScrolled() {
    if (!this.isLoading() && !this.isEnd()) {
      this.currentPage++;
      this.find();
    }
  }

  onAction(item: SelectorListItem) {
    this.dialogRef.close(item);
  }

  onClose() {
    this.dialogRef.close();
  }
}
